import { Empty, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React from 'react';
import {
  type AccessLog,
  type AccessUniqueUser,
  type ClientInfo,
  type GeoLocation,
  getAccessLogs,
  getAccessUniqueUsers,
} from '@/services/auth/accessLog';

const { Text } = Typography;

const shortFp = (fp?: string) => (fp ? `…${fp.slice(-8)}` : '—');
const autoTag = (c?: ClientInfo) =>
  c?.webdriver ? <Tag color="error">自动化</Tag> : null;
const fmtGeo = (g?: GeoLocation) => {
  if (!g) return '—';
  const p = [g.country, g.region, g.city].filter(Boolean);
  return p.length ? p.join(' / ') : '—';
};
const fmtDevice = (r: { device?: string; os?: string; browser?: string }) => {
  const p = [r.device, r.os, r.browser].filter(Boolean);
  return p.length ? p.join(' · ') : '—';
};

// 复用访问日志接口,展示该用户的设备(按指纹去重)+ 最近访问明细。无新接口。
const AccessPanel: React.FC<{ userId: string }> = ({ userId }) => {
  const [loading, setLoading] = React.useState(false);
  const [summary, setSummary] = React.useState<AccessUniqueUser | null>(null);
  const [devices, setDevices] = React.useState<AccessUniqueUser[]>([]);
  const [recent, setRecent] = React.useState<AccessLog[]>([]);

  React.useEffect(() => {
    if (!userId) return;
    let alive = true;
    setLoading(true);
    // allSettled:任一接口失败不影响其余两段展示
    Promise.allSettled([
      getAccessUniqueUsers({ user_id: userId, group_by: 'user', page_size: 1 }),
      getAccessUniqueUsers({
        user_id: userId,
        group_by: 'fingerprint',
        page_size: 50,
      }),
      getAccessLogs({ user_id: userId, page: 1, page_size: 30 }),
    ])
      .then(([s, d, r]) => {
        if (!alive) return;
        if (s.status === 'fulfilled' && s.value.success)
          setSummary(s.value.data?.list?.[0] || null);
        if (d.status === 'fulfilled' && d.value.success)
          setDevices(d.value.data?.list || []);
        if (r.status === 'fulfilled' && r.value.success)
          setRecent(r.value.data?.list || []);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [userId]);

  const deviceCols: ColumnsType<AccessUniqueUser> = [
    {
      title: '指纹',
      dataIndex: 'fingerprint',
      width: 160,
      render: (fp: string, r) => (
        <span style={{ fontFamily: 'monospace' }}>
          {shortFp(fp)} {autoTag(r.client)}
        </span>
      ),
    },
    { title: '访问次数', dataIndex: 'visits', width: 90 },
    { title: 'IP数', dataIndex: 'ip_count', width: 70 },
    {
      title: '最近访问',
      dataIndex: 'last_seen',
      width: 150,
      render: (v: string) => dayjs(v).format('MM-DD HH:mm:ss'),
    },
    { title: '设备', width: 180, render: (_, r) => fmtDevice(r) },
  ];

  const recentCols: ColumnsType<AccessLog> = [
    {
      title: '时间',
      dataIndex: 'created_at',
      width: 150,
      render: (v: string) => dayjs(v).format('MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      dataIndex: 'operation_type',
      width: 110,
      render: (t: string) => <Tag>{t}</Tag>,
    },
    {
      title: '路径',
      dataIndex: 'path',
      ellipsis: true,
      render: (p: string, r) => (
        <span>
          <Tag>{r.method}</Tag>
          {p}
        </span>
      ),
    },
    { title: 'IP', dataIndex: 'ip', width: 130 },
    { title: '地区', width: 170, render: (_, r) => fmtGeo(r.geo) },
  ];

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Space size="large" wrap>
        <Text>
          设备数 <Text strong>{summary?.fp_count ?? 0}</Text>
        </Text>
        <Text>
          IP数 <Text strong>{summary?.ip_count ?? 0}</Text>
        </Text>
        <Text>
          访问 <Text strong>{summary?.visits ?? 0}</Text>
        </Text>
        <Text>
          操作 <Text strong>{summary?.actions ?? 0}</Text>
        </Text>
      </Space>

      <div>
        <Text strong>设备(按指纹)</Text>
        <Table<AccessUniqueUser>
          style={{ marginTop: 8 }}
          rowKey={(r) => r.fingerprint || ''}
          loading={loading}
          columns={deviceCols}
          dataSource={devices}
          size="small"
          pagination={false}
          scroll={{ x: 650 }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        />
      </div>

      <div>
        <Text strong>最近访问</Text>
        <Table<AccessLog>
          style={{ marginTop: 8 }}
          rowKey={(r) => `${r.created_at}-${r.path}-${r.request_id || ''}`}
          loading={loading}
          columns={recentCols}
          dataSource={recent}
          size="small"
          pagination={false}
          scroll={{ x: 700, y: 280 }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        />
      </div>
    </Space>
  );
};

export default AccessPanel;
